import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { SelectOption } from "~/__generated__/graphql/graphql";
import GaiaPageComponent from "~/modules/app/components/GaiaPageComponent";
import GaiaPageLayoutColumn from "~/modules/app/components/GaiaPageLayoutColumn";
import useClientOrProjectScope from "~/modules/app/hooks/useClientOrProjectScope";
import useCurrentClient from "~/modules/client/hooks/useCurrentClient";
import useCurrentProject from "~/modules/project/hooks/useCurrentProject";

import ProtocolForm, { ProtocolFormValues } from "../../components/ProtocolForm";
import useProtocolListRoute_GetAll from "../ProtocolListRoute/api/useProtocolListRoute_GetAll";
import useProtocolCreateRoute_Create from "./api/useProtocolCreateRoute_Create";

export default function ProtocolCreateRoute() {
    const { t } = useTranslation("protocol");
    const navigate = useNavigate();

    const client = useCurrentClient();
    const project = useCurrentProject();

    const creationScope = useClientOrProjectScope();
    const { createProtocol, error } = useProtocolCreateRoute_Create();

    const isProjectScope = creationScope.project && !creationScope.client;
    const title = isProjectScope ? t("protocolCreateScreen.projectScopeTitle") : t("protocolCreateScreen.title");

    const { data: allProtocols } = useProtocolListRoute_GetAll({
        clientId: client?.client?.id,
        projectId: project?.data?.project?.id,
        templates: true,
    });

    const { templateOptions, protocolTemplates } = useMemo(() => {
        const templates = allProtocols ?? [];
        const options = templates.map(
            template =>
                ({
                    __typename: "SelectOption",
                    label: template.name || template.id,
                    value: template.id,
                }) as SelectOption,
        );

        return {
            templateOptions: options,
            protocolTemplates: templates,
        };
    }, [allProtocols]);

    const onSubmit = async (data: ProtocolFormValues) => {
        const result = await createProtocol({
            name: data.name,
            description: data.description,
            researchTypeId: data.researchTypeId,
            templateProtocolId: data.templateId,
            clientId: creationScope?.client?.id,
            projectId: creationScope?.project?.id,
            collectAllMediaPoints: data.collectAllMediaPoints === "collect-all",
            requiresRouteShuffling: data.requiresRouteShuffling,
        });

        const newId = result.data?.protocolCreate?.id;
        if (newId) {
            navigate(`../${newId}`, { replace: true });
        }
    };

    return (
        <GaiaPageLayoutColumn>
            <GaiaPageComponent title={title}>
                <ProtocolForm
                    onSubmit={onSubmit}
                    error={error}
                    protocolTemplates={protocolTemplates}
                    templateOptions={templateOptions}
                />
            </GaiaPageComponent>
        </GaiaPageLayoutColumn>
    );
}
